import { Alert, Grid, Paper, Typography } from '@mui/material';

import type { LlmCostSummary } from '@/lib/types/dashboard/llmCost';
import { formatTokens, formatUsd } from './format';

const metricCardSx = {
  p: 2,
  textAlign: 'center' as const,
  height: '100%',
  transition: 'box-shadow 0.2s ease',
  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
};

const metricValueSx = {
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums' as const,
  letterSpacing: '-0.02em',
};

function Card({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
      <Paper sx={metricCardSx}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ ...metricValueSx, color }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </Paper>
    </Grid>
  );
}

/** KPI カード群（コスト合計・ジョブ数・成功・失敗・入出力トークン・コスト不明） */
export function LlmCostSummaryCards({ summary }: { summary: LlmCostSummary }) {
  return (
    <>
      {summary.costUnknownCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          コスト不明のジョブが {summary.costUnknownCount} 件あります（時間切れ・キャンセルなどで
          Agent の結果を受け取れなかったため、合計コストに含まれていません）。
        </Alert>
      )}
      <Grid container spacing={2}>
        <Card
          label="LLM コスト合計"
          value={formatUsd(summary.totalCostUsd)}
          sub="SDK 推定値（Bedrock 実請求ではない）"
          color="#6366f1"
        />
        <Card label="ジョブ数" value={summary.jobCount.toLocaleString()} />
        <Card
          label="成功"
          value={summary.succeededCount.toLocaleString()}
          color={summary.succeededCount > 0 ? '#10b981' : undefined}
        />
        <Card
          label="失敗"
          value={summary.failedCount.toLocaleString()}
          color={summary.failedCount > 0 ? '#ef4444' : undefined}
        />
        <Card
          label="トークン（入力 / 出力）"
          value={`${formatTokens(summary.totalInputTokens)} / ${formatTokens(summary.totalOutputTokens)}`}
          sub="キャッシュの読み書きを含まない"
        />
        <Card
          label="コスト不明"
          value={summary.costUnknownCount.toLocaleString()}
          color={summary.costUnknownCount > 0 ? '#f59e0b' : undefined}
        />
      </Grid>
    </>
  );
}
